
import React, { useState } from 'react';
import { 
  X, 
  Zap, 
  LayoutTemplate, 
  Printer, 
  Save, 
  FileText, 
  Search, 
  Download, 
  Upload,
  BookOpen,
  MousePointer2,
  Sparkles,
  FileDown,
  Layers,
  Filter,
  Edit2,
  Image as ImageIcon,
  List,
  Building,
  FolderOpen
} from 'lucide-react';

interface WikiModalProps {
  onClose: () => void;
}

export const WikiModal: React.FC<WikiModalProps> = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('intro');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(`wiki-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'intro', label: 'Start & Navigation', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'project', label: 'Projekt & Stammdaten', icon: <Building className="w-4 h-4" /> },
    { id: 'catalog', label: 'Katalog & Verwaltung', icon: <FolderOpen className="w-4 h-4" /> },
    { id: 'generator', label: 'Smart-Generator (PDF)', icon: <Zap className="w-4 h-4" /> },
    { id: 'print', label: 'Drucken & Export', icon: <Printer className="w-4 h-4" /> },
    { id: 'data', label: 'Speichern & Laden', icon: <Save className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative">
        
        {/* Mobile Header / Close Button */}
        <div className="absolute top-4 right-4 z-50">
          <button 
            onClick={onClose}
            className="p-2 bg-white/80 hover:bg-slate-100 backdrop-blur rounded-full text-slate-500 hover:text-red-500 transition-colors shadow-sm border border-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* SIDEBAR NAVIGATION */}
        <div className="w-full md:w-64 bg-slate-50 border-r border-slate-200 flex-shrink-0 flex flex-col">
          <div className="p-6 border-b border-slate-200 bg-white">
            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
              <span className="bg-blue-600 text-white p-1.5 rounded-lg"><BookOpen className="w-5 h-5" /></span>
              Hilfe & Wiki
            </h2>
            <p className="text-xs text-slate-500 mt-2 font-medium">Dokumentation v1.3</p>
          </div>
          
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                  activeSection === section.id 
                    ? 'bg-blue-100 text-blue-700 shadow-sm' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {section.icon}
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* CONTENT AREA */}
        <div className="flex-1 overflow-y-auto bg-white relative scroll-smooth" id="wiki-scroll-container">
          <div className="max-w-4xl mx-auto p-8 md:p-12 space-y-16">

            {/* INTRO */}
            <section id="wiki-intro" className="scroll-mt-8">
              <div className="mb-6">
                <span className="text-blue-600 font-bold tracking-widest text-xs uppercase bg-blue-50 px-2 py-1 rounded inline-block mb-3">Überblick</span>
                <h1 className="text-4xl font-bold text-slate-900 mb-4">Willkommen im RAB 32 Generator</h1>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Diese Anwendung erstellt professionelle "Unterlagen für spätere Arbeiten" gemäß Baustellenverordnung.
                  Die Benutzeroberfläche ist in zwei Hauptbereiche unterteilt:
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="border border-slate-200 rounded-xl p-4 flex gap-3 items-start hover:bg-slate-50 transition-colors">
                    <div className="bg-blue-100 text-blue-600 p-2 rounded-lg"><Building className="w-5 h-5" /></div>
                    <div>
                        <h3 className="font-bold text-slate-800">Tab 1: Projekt & Daten</h3>
                        <p className="text-sm text-slate-600 mt-1">Hier pflegen Sie Stammdaten (Bauherr, Standort), laden Bilder hoch und bearbeiten die Vorbemerkungen.</p>
                    </div>
                 </div>
                 <div className="border border-slate-200 rounded-xl p-4 flex gap-3 items-start hover:bg-slate-50 transition-colors">
                    <div className="bg-blue-100 text-blue-600 p-2 rounded-lg"><FolderOpen className="w-5 h-5" /></div>
                    <div>
                        <h3 className="font-bold text-slate-800">Tab 2: Maßnahmenkatalog</h3>
                        <p className="text-sm text-slate-600 mt-1">Das Herzstück: Hier erstellen, generieren und verwalten Sie die eigentlichen Wartungspunkte und Sicherheitsmaßnahmen.</p>
                    </div>
                 </div>
              </div>
            </section>

            <hr className="border-slate-100" />

            {/* PROJECT DATA */}
            <section id="wiki-project" className="scroll-mt-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                 <LayoutTemplate className="w-6 h-6 text-slate-400" />
                 Projekt & Stammdaten
              </h2>
              
              <div className="space-y-8">
                 <div className="flex gap-4 items-start">
                    <div className="bg-blue-50 p-2 rounded-lg text-blue-600 flex-shrink-0 mt-1">
                        <Search className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 mb-1">Adress-Suche (Autocomplete)</h3>
                        <p className="text-slate-600 text-sm">
                            Die Felder "Standort" und "Anschrift" sind mit einer Datenbank verbunden. 
                            Tippen Sie die ersten 3 Buchstaben einer Straße oder Stadt ein, um Vorschläge aus der D-A-CH Region zu erhalten.
                        </p>
                    </div>
                 </div>

                 <div className="flex gap-4 items-start">
                    <div className="bg-blue-50 p-2 rounded-lg text-blue-600 flex-shrink-0 mt-1">
                        <MousePointer2 className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 mb-1">Bilder per Drag & Drop</h3>
                        <p className="text-slate-600 text-sm">
                            Ziehen Sie ein Projektbild oder Ihr Firmenlogo einfach in die gestrichelten Boxen. 
                            <strong>Automatische Optimierung:</strong> Bilder werden automatisch komprimiert, um die Projektdatei klein und speicherbar zu halten.
                        </p>
                    </div>
                 </div>

                 <div className="flex gap-4 items-start">
                    <div className="bg-blue-50 p-2 rounded-lg text-blue-600 flex-shrink-0 mt-1">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 mb-1">Vorbemerkungen & Rechtliches</h3>
                        <p className="text-slate-600 text-sm">
                           Im unteren Bereich finden Sie die Texte für die Einführung (Seite 2+). Klicken Sie zum Aufklappen.
                           Verwenden Sie <code>[UMBRUCH]</code> im Text, um manuell eine neue Seite zu erzwingen.
                        </p>
                    </div>
                 </div>
              </div>
            </section>

            <hr className="border-slate-100" />

            {/* CATALOG & LIST MANAGEMENT */}
            <section id="wiki-catalog" className="scroll-mt-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                 <FolderOpen className="w-6 h-6 text-slate-400" />
                 Maßnahmenkatalog verwalten
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white border border-slate-200 p-5 rounded-xl">
                      <h4 className="font-bold flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-purple-600" /> KI-Vorschlag (Einzeln)</h4>
                      <p className="text-sm text-slate-600">
                          Geben Sie beim manuellen Eintrag nur das Bauteil ein (z.B. "Dachfenster") und klicken Sie auf <strong>KI-Vorschlag</strong>.
                          Die KI füllt Kategorie, Gefährdungen, Maßnahmen, Turnus und Regelwerke automatisch aus.
                      </p>
                  </div>
                  <div className="bg-white border border-slate-200 p-5 rounded-xl">
                      <h4 className="font-bold flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-blue-600" /> Filter & Übersicht</h4>
                      <p className="text-sm text-slate-600">
                          Nutzen Sie die Filter-Tabs oben in der Liste (z.B. "Nur Dach"), um den Überblick bei großen Projekten zu behalten.
                          Klicken Sie auf eine Kategorie-Überschrift, um die Gruppe ein- oder auszuklappen.
                      </p>
                  </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <h4 className="font-bold flex items-center gap-2 mb-3"><Edit2 className="w-4 h-4 text-slate-600" /> Bearbeiten & Umbenennen</h4>
                  <ul className="space-y-3 text-sm text-slate-600">
                      <li className="flex gap-2">
                          <span className="font-semibold text-slate-800">Eintrag bearbeiten:</span>
                          Klicken Sie auf das Stift-Symbol bei einem Eintrag, um alle Felder direkt in der Liste anzupassen.
                      </li>
                      <li className="flex gap-2">
                          <span className="font-semibold text-slate-800">Kategorie umbenennen:</span>
                          Klicken Sie auf den Stift neben einem Kategorienamen (z.B. "SONSTIGES"), um die gesamte Gruppe umzubenennen (z.B. in "TIEFGARAGE"). Alle zugehörigen Einträge werden aktualisiert.
                      </li>
                  </ul>
              </div>
            </section>

            <hr className="border-slate-100" />

            {/* SMART GENERATOR */}
            <section id="wiki-generator" className="scroll-mt-8">
               <div className="flex items-center gap-3 mb-6">
                   <h2 className="text-2xl font-bold text-slate-900">Smart-Generator & Checklisten</h2>
                   <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[11px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">Pro Feature</span>
               </div>

               <p className="text-slate-600 mb-6">
                   Erstellen Sie ganze Kataloge in Sekunden. Öffnen Sie dazu den Bereich <strong>"Smart-Generator & Vorlagen"</strong> im Maßnahmenkatalog-Tab.
               </p>

               <div className="space-y-6">
                  <div className="flex items-start gap-4">
                      <div className="bg-slate-100 p-2 rounded-full font-bold text-slate-600 w-8 h-8 flex items-center justify-center flex-shrink-0">A</div>
                      <div>
                          <h4 className="font-bold text-slate-800">Aus Freitext generieren</h4>
                          <p className="text-sm text-slate-600">
                              Beschreiben Sie das Gebäude in eigenen Worten (z.B. "Bürogebäude mit 5 Etagen, Flachdach, Aufzug und Tiefgarage"). 
                              Die KI leitet daraus alle relevanten Wartungspunkte ab.
                          </p>
                      </div>
                  </div>

                  <div className="flex items-start gap-4">
                      <div className="bg-slate-100 p-2 rounded-full font-bold text-slate-600 w-8 h-8 flex items-center justify-center flex-shrink-0">B</div>
                      <div>
                          <h4 className="font-bold text-slate-800">PDF-Checklisten Workflow (Kunden-Integration)</h4>
                          <ol className="list-decimal list-inside text-sm text-slate-600 mt-2 space-y-2">
                              <li>Wählen Sie eine Vorlage (Wohnbau, Industrie, etc.) und klicken Sie auf <Download className="w-3 h-3 inline" />.</li>
                              <li>Senden Sie die PDF an den Kunden oder Architekten. Diese können die Datei <strong>digital ausfüllen</strong> (Ankreuzen).</li>
                              <li>Laden Sie die ausgefüllte PDF wieder hoch (<Upload className="w-3 h-3 inline" />).</li>
                              <li>Das System erkennt die Markierungen und generiert automatisch den passenden Katalog.</li>
                          </ol>
                      </div>
                  </div>
               </div>
            </section>

            <hr className="border-slate-100" />

            {/* PRINT & PREVIEW */}
            <section id="wiki-print" className="scroll-mt-8">
               <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Printer className="w-6 h-6 text-slate-400" /> Druckvorschau & Einstellungen
               </h2>
               
               <p className="text-slate-600 mb-6">
                   Klicken Sie auf <strong>Vorschau</strong> oben rechts, um das Dokument zu prüfen. Hier stehen Ihnen wichtige Layout-Optionen zur Verfügung:
               </p>

               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                   <div className="border p-4 rounded-lg">
                       <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-blue-500" /> Bildgröße Deckblatt</h4>
                       <p className="text-xs text-slate-500">
                           Wählen Sie zwischen Klein, Mittel oder Groß. Das Layout passt sich automatisch an, damit Fußzeilen und Titel nicht verrutschen.
                       </p>
                   </div>
                   <div className="border p-4 rounded-lg">
                       <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><List className="w-4 h-4 text-blue-500" /> Inhaltsverzeichnis</h4>
                       <p className="text-xs text-slate-500">
                           Schalten Sie das Inhaltsverzeichnis (Seite 2) an oder aus. Es aktualisiert sich dynamisch mit allen Unterpunkten.
                       </p>
                   </div>
                   <div className="border p-4 rounded-lg">
                       <h4 className="font-bold text-sm mb-2 flex items-center gap-2"><Layers className="w-4 h-4 text-blue-500" /> Regelwerke drucken</h4>
                       <p className="text-xs text-slate-500">
                           Entscheiden Sie, ob die Spalte "Regelwerke" im Katalog gedruckt werden soll. Wenn deaktiviert, erhalten die anderen Spalten mehr Platz.
                       </p>
                   </div>
               </div>

               <div className="bg-yellow-50 border border-yellow-100 p-4 rounded-lg text-sm text-yellow-800 flex items-start gap-3">
                   <Printer className="w-5 h-5 flex-shrink-0 mt-0.5" />
                   <div>
                       <strong>Druck-Tipp:</strong> Wählen Sie im System-Druckdialog "Als PDF speichern". 
                       Achten Sie darauf, dass <strong>"Hintergrundgrafiken drucken"</strong> aktiviert ist und die Ränder auf "Keine" oder "Minimal" eingestellt sind, für ein randloses Design.
                   </div>
               </div>
            </section>

            <hr className="border-slate-100" />

            {/* DATA MANAGEMENT */}
            <section id="wiki-data" className="scroll-mt-8">
               <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Save className="w-6 h-6 text-slate-400" /> Speichern & Laden
               </h2>
               <ul className="space-y-4 text-sm text-slate-600">
                   <li className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                       <div className="bg-white p-2 rounded border border-slate-200"><FileDown className="w-4 h-4 text-blue-500" /></div>
                       <div>
                           <strong>Projekt Speichern:</strong> Lädt eine <code>.json</code> Datei auf Ihren PC herunter. Diese enthält alle Texte, Einstellungen und Bilder.
                       </div>
                   </li>
                   <li className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                       <div className="bg-white p-2 rounded border border-slate-200"><Upload className="w-4 h-4 text-blue-500" /></div>
                       <div>
                           <strong>Projekt Laden:</strong> Stellt einen gespeicherten Projektstand wieder her. Die Versionsnummer wird dabei automatisch hochgezählt (z.B. 1.0 → 1.1).
                       </div>
                   </li>
                   <li className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                       <div className="bg-white p-2 rounded border border-slate-200"><Save className="w-4 h-4 text-green-500" /></div>
                       <div>
                           <strong>Auto-Save:</strong> Die App speichert Ihre Eingaben automatisch im Browser-Speicher (LocalStorage). Wenn Sie den Tab schließen und wieder öffnen, sind Ihre Daten noch da.
                       </div>
                   </li>
               </ul>
            </section>

            <div className="h-24"></div> {/* Spacer at bottom */}
          </div>
        </div>
      </div>
    </div>
  );
};
